import { useState, useEffect } from "react";
import { Settings2, Eye, EyeOff, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";

export interface ColumnVisibilityConfig {
  [key: string]: {
    label: string;
    visible: boolean;
  };
}

interface ColumnVisibilityProps {
  columns: ColumnVisibilityConfig;
  onColumnVisibilityChange: (columns: ColumnVisibilityConfig) => void;
  initialConfig: ColumnVisibilityConfig;
  onReset?: () => void;
}

export function ColumnVisibility({
  columns,
  onColumnVisibilityChange,
  initialConfig,
  onReset,
}: ColumnVisibilityProps) {
  const handleReset = () => {
    onColumnVisibilityChange(initialConfig);
    onReset?.();
  };

  const handleColumnToggle = (key: string) => {
    const newColumns = {
      ...columns,
      [key]: { ...columns[key], visible: !columns[key].visible },
    };
    onColumnVisibilityChange(newColumns);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10 bg-slate-100 text-white hover:bg-slate-200"
        >
          <Settings2 className="bg-slate-100 text-slate-800" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52 py-3 px-2 shadow-lg border-slate-200 bg-white rounded-lg"
      >
        <div className="text-base font-medium text-slate-500 text-center">
          Columns
        </div>
        <div className="max-h-[300px] overflow-y-auto space-y-3 mt-2 px-3">
          {Object.entries(columns).map(([key, { label, visible }]) => (
            <button
              key={key}
              onClick={() => handleColumnToggle(key)}
              className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-md hover:bg-blue-50 transition-colors text-left
                ${visible ? "bg-blue-50" : ""}`}
            >
              <span className="text-slate-700">{label}</span>
              {visible ? (
                <Eye className="h-4 w-4 text-slate-400" />
              ) : (
                <EyeOff className="h-4 w-4 text-slate-300" />
              )}
            </button>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-slate-200">
          <button
            onClick={handleReset}
            className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function useColumnVisibility(
  initialConfig: ColumnVisibilityConfig,
  storageKey: string
) {
  // Initialize state from localStorage or initial config
  const [columns, setColumns] = useState<ColumnVisibilityConfig>(() => {
    const savedConfig = localStorage.getItem(storageKey);
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        // Validate that the saved config matches the structure of initialConfig
        const isValidConfig = Object.keys(initialConfig).every(
          (key) => key in parsedConfig
        );
        return isValidConfig ? parsedConfig : initialConfig;
      } catch (e) {
        console.error("Failed to parse saved column visibility config:", e);
        return initialConfig;
      }
    }
    return initialConfig;
  });

  // Update localStorage whenever columns change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(columns));
  }, [columns, storageKey]);

  const isColumnVisible = (key: string) => columns[key]?.visible ?? true;

  return {
    columns,
    setColumns,
    isColumnVisible,
  };
}
